type QueueItem<T> =
    | { type: 'value'; value: T }
    | { type: 'error'; error: Error }
    | { type: 'completion' };

interface Consumer<T> {
    queue: QueueItem<T>[];
    signal?: () => void;
}

export class GeneratorFanout<T> {
    private consumers: Set<Consumer<T>> = new Set();
    private state: 'idle' | 'distributing' | 'completed' | 'error' = 'idle';
    private rootGenerator: AsyncGenerator<T>;
    // Single historical queue that stores all events for replay
    private historicalQueue: QueueItem<T>[] = [];

    constructor(rootGenerator: AsyncGenerator<T>) {
        this.rootGenerator = rootGenerator;
    }

    private async startDistributing() {
        if (this.state !== 'idle') {
            return;
        }

        this.state = 'distributing';

        try {
            for await (const value of this.rootGenerator) {
                this.distribute({ type: 'value', value });
            }
            this.distribute({ type: 'completion' });
            this.state = 'completed';
        } catch (error) {
            this.distribute({
                type: 'error',
                error
            });
            this.state = 'error';
        } finally {
            this.consumers.clear();
        }
    }

    async *generator(): AsyncGenerator<T> {
        // New consumer gets a copy of the entire historical queue
        const consumer: Consumer<T> = {
            queue: [...this.historicalQueue]
        };

        this.consumers.add(consumer);

        // Start distributing values if we haven't already
        this.startDistributing();

        yield* this.getResponsesForConsumer(consumer);
    }

    private distribute(item: QueueItem<T>): void {
        // Store in historical queue for new subscribers
        this.historicalQueue.push(item);

        // Distribute to all current consumers
        for (const consumer of this.consumers) {
            this.signalConsumer(consumer, item);
        }
    }

    private signalConsumer(consumer: Consumer<T>, item: QueueItem<T>): void {
        consumer.queue.push(item);
        if (consumer.signal) {
            consumer.signal();
            consumer.signal = undefined;
        }
    }

    private async *getResponsesForConsumer(
        consumer: Consumer<T>
    ): AsyncGenerator<T> {
        while (true) {
            if (consumer.queue.length > 0) {
                // Process queue items until empty
                while (consumer.queue.length > 0) {
                    const item = consumer.queue.shift()!;

                    if (item.type === 'value') {
                        yield item.value;
                    } else if (item.type === 'error') {
                        throw item.error;
                    } else if (item.type === 'completion') {
                        return; // Exit the generator
                    }
                }
            } else {
                await new Promise<void>(resolve => {
                    consumer.signal = resolve;
                });
            }
        }
    }
}
